import React, { Ref, useCallback, useContext, useEffect, useRef, useState } from 'react';
import Form from 'antd/es/form';
import { PhoneNumberUtil, PhoneNumberFormat } from 'google-libphonenumber';
import ReactPhoneInput from 'react-phone-input-2';

import SizeContext from 'antd/es/config-provider/SizeContext';
import { usePrevious } from 'utils/hooks/use-previous';
import { useCombinedRefs } from 'utils/hooks/use-combined-refs';

import 'react-phone-input-2/lib/high-res.css';
import './styles.css';

import type { InputProps } from 'antd/es/input';
import type { FormItemProps } from 'antd/lib/form/FormItem';
import { phoneValidator } from 'utils/validators';
import { Rule, NamePath } from 'rc-field-form/lib/interface';

interface IPhoneFormItem extends FormItemProps {
  label?: string;
  fieldName?: NamePath;
  rules?: Rule[];
  country?: string;
  inputProps?: IInputProps;
}

interface IInputProps extends Omit<InputProps, 'onChange'> {
  onChange?: (value: string) => void;
  onBlur?: (event: any) => void;
  value?: string;
  country?: string;
  useRawValue?: boolean;
}

const phoneUtil = PhoneNumberUtil.getInstance();

export const PhoneInput = React.forwardRef(
  ({ onChange = () => void 0, onBlur, value, country, useRawValue, ...props }: IInputProps, ref: Ref<any>) => {
    const innerRef = useRef<typeof ReactPhoneInput>(null);
    const combinedRef = useCombinedRefs(ref, innerRef);
    const [innerValue, setInnerValue] = useState<string | undefined>(undefined);
    const [rawInnerValue, setRawInnerValue] = useState<string | null>(null);
    const prevValue = usePrevious<string | undefined>(innerValue, undefined);
    const size = useContext(SizeContext);
    const isLarge = size === 'large';

    useEffect(() => {
      if (prevValue === undefined && value) {
        if (useRawValue) {
          try {
            const nationalNumber = phoneUtil.parseAndKeepRawInput(value)?.getNationalNumber()?.toString();
            setRawInnerValue(nationalNumber ?? null);
            setInnerValue(nationalNumber);
          } catch (e) {
            setRawInnerValue(value);
            setInnerValue(value);
          }
        } else {
          try {
            const phone = phoneUtil.parse(value);
            phone.setCountryCode(1);
            setInnerValue(phoneUtil.format(phone, PhoneNumberFormat.E164));
          } catch (e) {
            setInnerValue(value);
          }
        }
      }
    }, [prevValue, value, useRawValue]);

    useEffect(() => {
      if (rawInnerValue) {
        onChange(rawInnerValue);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rawInnerValue]);

    const onChangeRaw = useCallback(
      (text: string) => {
        setInnerValue(text);
        onChange(useRawValue ? text : '+' + text);
      },
      [onChange, setInnerValue, useRawValue]
    );

    return (
      <ReactPhoneInput
        value={innerValue}
        onBlur={onBlur}
        onChange={onChangeRaw}
        country="us"
        onlyCountries={['us']}
        disableCountryGuess={true}
        countryCodeEditable={!!useRawValue}
        disableCountryCode={!!useRawValue}
        disableDropdown={true}
        jumpCursorToEnd={false}
        containerClass="customStyle"
        searchPlaceholder="Type here..."
        searchNotFound="No entries to show"
        inputClass={isLarge ? 'ant-input-lg' : ''}
        // @ts-ignore
        ref={combinedRef}
        {...props}
      />
    );
  }
);

export const PhoneFormItem = ({
  country = 'us',
  rules = [],
  label,
  fieldName,
  inputProps,
  ...props
}: IPhoneFormItem) => (
  <Form.Item
    label={label}
    name={fieldName}
    validateTrigger={['onBlur']}
    rules={rules.concat([{ validator: phoneValidator }])}
    {...props}
  >
    <PhoneInput {...inputProps} />
  </Form.Item>
);

